export const PUBLICATION_INBOX_TIME_ZONE =
  'America/Sao_Paulo';

const publicationInboxNamedEntities:
  Record<string, string> = {
    amp: '&',
    apos: "'",
    quot: '"',
    lt: '<',
    gt: '>',
    nbsp: ' ',
    ordm: 'º',
    ordf: 'ª',
    Agrave: 'À',
    Aacute: 'Á',
    Acirc: 'Â',
    Atilde: 'Ã',
    Auml: 'Ä',
    Aring: 'Å',
    agrave: 'à',
    aacute: 'á',
    acirc: 'â',
    atilde: 'ã',
    auml: 'ä',
    aring: 'å',
    Ccedil: 'Ç',
    ccedil: 'ç',
    Egrave: 'È',
    Eacute: 'É',
    Ecirc: 'Ê',
    Euml: 'Ë',
    egrave: 'è',
    eacute: 'é',
    ecirc: 'ê',
    euml: 'ë',
    Igrave: 'Ì',
    Iacute: 'Í',
    Icirc: 'Î',
    Iuml: 'Ï',
    igrave: 'ì',
    iacute: 'í',
    icirc: 'î',
    iuml: 'ï',
    Ntilde: 'Ñ',
    ntilde: 'ñ',
    Ograve: 'Ò',
    Oacute: 'Ó',
    Ocirc: 'Ô',
    Otilde: 'Õ',
    Ouml: 'Ö',
    ograve: 'ò',
    oacute: 'ó',
    ocirc: 'ô',
    otilde: 'õ',
    ouml: 'ö',
    Ugrave: 'Ù',
    Uacute: 'Ú',
    Ucirc: 'Û',
    Uuml: 'Ü',
    ugrave: 'ù',
    uacute: 'ú',
    ucirc: 'û',
    uuml: 'ü',
    Yacute: 'Ý',
    yacute: 'ý',
    yuml: 'ÿ',
  };

export const decodePublicationInboxText = (
  value: string | null | undefined
) => {
  if (!value) {
    return value ?? '';
  }

  return value
    .replace(
      /&#(\d+);/g,
      (entity, decimal: string) => {
        const codePoint = Number(decimal);

        if (
          !Number.isInteger(codePoint) ||
          codePoint < 0 ||
          codePoint > 0x10ffff
        ) {
          return entity;
        }

        try {
          return String.fromCodePoint(codePoint);
        } catch {
          return entity;
        }
      }
    )
    .replace(
      /&#x([0-9a-f]+);/gi,
      (entity, hexadecimal: string) => {
        const codePoint =
          Number.parseInt(hexadecimal, 16);

        if (
          !Number.isInteger(codePoint) ||
          codePoint < 0 ||
          codePoint > 0x10ffff
        ) {
          return entity;
        }

        try {
          return String.fromCodePoint(codePoint);
        } catch {
          return entity;
        }
      }
    )
    .replace(
      /&([A-Za-z][A-Za-z0-9]+);/g,
      (entity, name: string) =>
        publicationInboxNamedEntities[name] ??
        entity
    );
};

export type PublicationInboxFolder =
  | 'today'
  | 'new'
  | 'review'
  | 'action'
  | 'deadlines'
  | 'hearings'
  | 'unlinked'
  | 'completed'
  | 'all';

export type PublicationInboxAttention =
  | 'critical'
  | 'high'
  | 'review'
  | 'informational'
  | 'resolved';

export type PublicationInboxState =
  | 'new'
  | 'ai_processing'
  | 'awaiting_review'
  | 'decision_required'
  | 'deadline_confirmation'
  | 'action_open'
  | 'completed'
  | 'exception';

export const publicationInboxFolderMeta: Array<{
  value: PublicationInboxFolder;
  label: string;
  icon: string;
}> = [
  { value: 'today', label: 'Hoje', icon: 'ri-sun-line' },
  { value: 'new', label: 'Novas', icon: 'ri-mail-unread-line' },
  { value: 'review', label: 'Revisão IA', icon: 'ri-brain-line' },
  { value: 'action', label: 'Exigem ação', icon: 'ri-flashlight-line' },
  { value: 'deadlines', label: 'Prazos possíveis', icon: 'ri-timer-line' },
  { value: 'hearings', label: 'Audiências', icon: 'ri-calendar-event-line' },
  { value: 'unlinked', label: 'Sem vínculo', icon: 'ri-link-unlink-m' },
  { value: 'completed', label: 'Concluídas', icon: 'ri-checkbox-circle-line' },
  { value: 'all', label: 'Todas', icon: 'ri-inbox-archive-line' },
];

export type PublicationInboxQueryParams = {
  folder: PublicationInboxFolder;
  search?: string;
  tribunal?: string;
  source?: string;
  attention?: PublicationInboxAttention | '';
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  cursor?: string;
};

export const buildPublicationInboxQueryString = (
  query: PublicationInboxQueryParams
) => {
  const params = new URLSearchParams();

  params.set('folder', query.folder);
  params.set(
    'limit',
    String(query.limit ?? 5)
  );

  const optionalEntries: Array<
    [string, string | undefined]
  > = [
    ['search', query.search],
    ['tribunal', query.tribunal],
    ['source', query.source],
    [
      'attention',
      query.attention || undefined,
    ],
    ['dateFrom', query.dateFrom],
    ['dateTo', query.dateTo],
    ['cursor', query.cursor],
  ];

  for (const [key, value] of optionalEntries) {
    const normalized =
      String(value ?? '').trim();

    if (normalized) {
      params.set(key, normalized);
    }
  }

  return params.toString();
};
export const publicationInboxStateLabel = (

  state: PublicationInboxState
) => {
  switch (state) {
    case 'new':
      return 'Nova';
    case 'ai_processing':
      return 'IA processando';
    case 'awaiting_review':
      return 'Aguardando revisão';
    case 'decision_required':
      return 'Decisão necessária';
    case 'deadline_confirmation':
      return 'Confirmar possível prazo';
    case 'action_open':
      return 'Em acompanhamento';
    case 'completed':
      return 'Concluída';
    case 'exception':
      return 'Exceção operacional';
  }
};

export const publicationInboxAttentionLabel = (
  attention: PublicationInboxAttention
) => {
  switch (attention) {
    case 'critical':
      return 'Crítica';
    case 'high':
      return 'Alta';
    case 'review':
      return 'Revisão';
    case 'informational':
      return 'Informativa';
    case 'resolved':
      return 'Resolvida';
  }
};

export const publicationInboxAttentionClasses = (
  attention: PublicationInboxAttention
) => {
  switch (attention) {
    case 'critical':
      return {
        dot: 'bg-red-500',
        border: 'border-red-200',
        badge: 'border-red-200 bg-red-50 text-red-700',
      };
    case 'high':
      return {
        dot: 'bg-amber-500',
        border: 'border-amber-200',
        badge: 'border-amber-200 bg-amber-50 text-amber-700',
      };
    case 'review':
      return {
        dot: 'bg-yellow-400',
        border: 'border-yellow-200',
        badge: 'border-yellow-200 bg-yellow-50 text-yellow-700',
      };
    case 'informational':
      return {
        dot: 'bg-sky-500',
        border: 'border-sky-200',
        badge: 'border-sky-200 bg-sky-50 text-sky-700',
      };
    case 'resolved':
      return {
        dot: 'bg-slate-400',
        border: 'border-background-200',
        badge: 'border-background-200 bg-background-100 text-foreground-600',
      };
  }
};

export const formatPublicationInboxDate = (
  value: string | null | undefined,
  includeTime = true
) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(
    'pt-BR',
    includeTime
      ? {
          dateStyle: 'short',
          timeStyle: 'short',
          timeZone: PUBLICATION_INBOX_TIME_ZONE,
        }
      : {
          dateStyle: 'short',
          timeZone: PUBLICATION_INBOX_TIME_ZONE,
        }
  ).format(date);
};

export const publicationInboxReferenceDate = (
  item: {
    availableAt: string | null;
    publishedAt: string | null;
    capturedAt: string;
  }
) =>
  item.availableAt ??
  item.publishedAt ??
  item.capturedAt;

export const publicationInboxDeadlineText = (
  value: number | null,
  unit: string | null
) => {
  if (!value) {
    return null;
  }

  if (unit === 'hours') {
    return `${value} hora${value === 1 ? '' : 's'}`;
  }

  return `${value} dia${value === 1 ? '' : 's'}`;
};